function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { transformToStatefulPromise } from "../fetchUtils/statefulPromise";
import ObservableData from "./ObservableData";
import shouldGoToNetwork from "../queryUtils/shouldGoToNetwork";
import { reportRuntimeError } from "../telemetry/internalReporting";
import { getOperationName } from "../../shared/utils/gql";
class ObservableQuery extends ObservableData {
    subscribe(observer, apolloLinkContext) {
        this.observers.add(observer);
        this.latestApolloLinkContext = apolloLinkContext;
        if (shouldGoToNetwork(this.query, this.variables, this.resultStore, this.memoryCache)) {
            if (!this.promise || this.promise.status !== 'pending') {
                this.setIsPending(true);
                this.promise = this.fetch();
            }
        }
        const watchCleanupFn = this.watch();
        return {
            unsubscribe: ()=>{
                this.observers.delete(observer);
                watchCleanupFn();
                // If this was the last observer, cleanup any in-flight subscription
                if (this.observers.size === 0 && this.currentApolloSubscription) {
                    this.currentApolloSubscription.unsubscribe();
                    this.currentApolloSubscription = null;
                    this.promise = null;
                }
            }
        };
    }
    refetch(apolloLinkContext) {
        if (!this.promise || this.promise.status !== 'pending') {
            this.setIsPending(true);
            if (apolloLinkContext) {
                this.latestApolloLinkContext = apolloLinkContext;
            }
            this.promise = this.fetch();
        }
    }
    fetch() {
        return transformToStatefulPromise(new Promise((resolve)=>{
            if (!this.latestApolloLinkContext) {
                const operationName = getOperationName(this.query);
                reportRuntimeError(this.observabilityFns, `ObservableQuery is missing Apollo Link Context for operation ${operationName}`);
                resolve();
                return;
            }
            this.currentApolloSubscription = this.apolloLinkExecutor.execute(this.query, this.variables, this.latestApolloLinkContext, {
                next: (result)=>{
                    this.setIsPending(false);
                    this.resultStore.setFetchingResults(this.query, this.variables, {
                        data: result.data,
                        errors: result.errors
                    });
                    if (result.errors) {
                        // Since ApolloLinkExecutor won't reach to the complete callback in
                        // the case of errors, we resolve the promise here.
                        resolve();
                    }
                },
                complete: ()=>{
                    this.currentApolloSubscription = null;
                    resolve();
                }
            });
        }));
    }
    startAsyncWaitOnResult(apolloLinkContext) {
        if (this.promise && this.promise.status !== 'fulfilled') {
            return this.promise;
        }
        this.latestApolloLinkContext = apolloLinkContext;
        if (!shouldGoToNetwork(this.query, this.variables, this.resultStore, this.memoryCache)) {
            const alreadyResolved = transformToStatefulPromise(Promise.resolve());
            // Force the 'fulfilled' status based on latest official recommendations
            // https://github.com/reactjs/react.dev/pull/8125
            if (alreadyResolved.status === 'pending') {
                Object.assign(alreadyResolved, {
                    status: 'fulfilled',
                    value: undefined
                });
            }
            this.promise = alreadyResolved;
            return this.promise;
        }
        this.setIsPending(true);
        this.promise = this.fetch();
        return this.promise;
    }
    constructor(query, variables, resultStore, memoryCache, apolloLinkExecutor, observabilityFns){
        super(query, variables, resultStore);
        // Used to avoid requests from multiple queries mounted at the same time
        _define_property(this, "promise", null);
        _define_property(this, "currentApolloSubscription", null);
        _define_property(this, "apolloLinkExecutor", void 0);
        _define_property(this, "latestApolloLinkContext", null);
        _define_property(this, "observabilityFns", void 0);
        _define_property(this, "memoryCache", void 0);
        this.memoryCache = memoryCache;
        this.apolloLinkExecutor = apolloLinkExecutor;
        this.observabilityFns = observabilityFns;
    }
}
export { ObservableQuery as default };

//# sourceMappingURL=ObservableQuery.js.map